import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Loader2, Plus, X } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui';
import { getErrorMessage } from '@/api';
import {
  getProductMethodUsage,
  type MethodUsage,
  type OfferedMethod,
  type PriceRowParams,
} from '@/api/admin';
import {
  useOfferProductMethod,
  useWithdrawProductMethod,
} from '@/hooks/useAdminProductPrices';
import { cn } from '@/lib/utils';

const TOAST_OPTS = { position: 'bottom-right' as const };

/** Same height as the price field so the row keeps its height; narrow so a
 * row of decoration columns stays within the viewport. */
const ICON_BUTTON =
  'inline-flex h-8 w-6 shrink-0 items-center justify-center rounded-control text-mute transition-colors hover:bg-paper-3 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary aria-disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:hover:bg-transparent aria-disabled:hover:text-mute';

export interface MethodWithdrawDialogProps {
  /** The dialog is open while a usage is set. */
  usage: MethodUsage | null;
  productName: string;
  methodName: string;
  /** Blocks "Withdraw" while the request runs. */
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirmation before a method leaves every active position of a product
 * (R18): how many seller designs use it, explicitly or through the
 * position default, which positions get a new default and — highlighted —
 * where embroidery would become that default.
 */
export function MethodWithdrawDialog({
  usage,
  productName,
  methodName,
  pending,
  onConfirm,
  onClose,
}: MethodWithdrawDialogProps) {
  const embroideryOn =
    usage?.positionsLosingDefault.filter(
      (position) => position.embroideryBecomesDefault
    ) ?? [];

  return (
    <Dialog
      open={usage !== null}
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
    >
      <DialogContent closeLabel="Close">
        <DialogTitle>Withdraw {methodName}?</DialogTitle>
        <DialogDescription>
          {methodName} will no longer be offered on any active position of{' '}
          <span className="font-semibold text-ink">{productName}</span>.
          Existing designs keep their configuration and price.
        </DialogDescription>

        {usage && (
          <div className="flex flex-col gap-3 text-sm text-ink-2">
            <p data-testid="withdraw-usage">
              Used by{' '}
              <span className="font-semibold text-ink">
                {usage.explicit} explicit
              </span>
              ,{' '}
              <span className="font-semibold text-ink">
                {usage.implicit} via default
              </span>{' '}
              active seller designs. Designs that relied on the default are
              pinned to {methodName}.
            </p>

            {usage.positionsLosingDefault.length > 0 && (
              <div>
                <p className="dc-eyebrow mb-1">New default</p>
                <ul className="flex flex-col gap-1">
                  {usage.positionsLosingDefault.map((position) => (
                    <li
                      key={position.positionId}
                      className={cn(
                        'flex items-center gap-2 rounded-control px-2 py-1 ring-1 ring-inset',
                        position.embroideryBecomesDefault
                          ? 'bg-[color-mix(in_srgb,var(--warning)_12%,transparent)] text-ink ring-warning'
                          : 'ring-line'
                      )}
                    >
                      {position.embroideryBecomesDefault && (
                        <AlertTriangle
                          className="h-4 w-4 shrink-0 text-warning"
                          aria-hidden="true"
                        />
                      )}
                      <span>
                        {position.newDefaultCode === null
                          ? `${position.name}: no method left to pick`
                          : position.embroideryBecomesDefault
                            ? `Embroidery becomes default on ${position.name}`
                            : `${position.newDefaultCode} becomes default on ${position.name}`}
                      </span>
                    </li>
                  ))}
                </ul>
                {embroideryOn.length > 0 && (
                  <p className="mt-2 text-xs text-warning">
                    Embroidery as a default changes what new designs are priced
                    with. Pick another default on the product page if that is
                    not intended.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={pending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="danger"
            loading={pending}
            loadingLabel="Withdrawing…"
            onClick={onConfirm}
          >
            Withdraw
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface WithdrawMethodButtonProps {
  productId: string;
  productName: string;
  method: OfferedMethod;
  priceRowParams: PriceRowParams;
}

/**
 * The × next to a method field (R13a, R13b, R18). Locked with
 * `aria-disabled` — not `disabled`, so the tooltip stays reachable — when
 * the method is the last one on a position. Otherwise a click loads the
 * usage (spinner, locked meanwhile) and opens the confirmation; a usage
 * error is a toast, never a dialog with missing numbers.
 */
export function WithdrawMethodButton({
  productId,
  productName,
  method,
  priceRowParams,
}: WithdrawMethodButtonProps) {
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usage, setUsage] = useState<MethodUsage | null>(null);
  const loadingRef = useRef(false);
  const withdraw = useWithdrawProductMethod(priceRowParams);

  const locked = method.lastMethodOn.length > 0;
  const lockReason = locked
    ? `Last method on ${method.lastMethodOn.length === 1 ? '1 position' : `${method.lastMethodOn.length} positions`}`
    : undefined;

  const openDialog = async () => {
    if (locked || loadingRef.current) return;
    loadingRef.current = true;
    setLoadingUsage(true);
    try {
      setUsage(await getProductMethodUsage(productId, method.methodId));
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    } finally {
      loadingRef.current = false;
      setLoadingUsage(false);
    }
  };

  const confirm = async () => {
    if (withdraw.isPending) return;
    try {
      await withdraw.mutateAsync({ productId, methodId: method.methodId });
      toast.success(
        `${method.name} withdrawn from "${productName}"`,
        TOAST_OPTS
      );
      setUsage(null);
    } catch (error) {
      // A 409 (last method on a position) arrives with the server's text.
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label={`Withdraw ${method.name} from ${productName}`}
        aria-disabled={locked || loadingUsage || undefined}
        aria-busy={loadingUsage || undefined}
        title={lockReason}
        onClick={() => void openDialog()}
        className={ICON_BUTTON}
      >
        {loadingUsage ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <X className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
      <MethodWithdrawDialog
        usage={usage}
        productName={productName}
        methodName={method.name}
        pending={withdraw.isPending}
        onConfirm={() => void confirm()}
        onClose={() => setUsage(null)}
      />
    </>
  );
}

export interface OfferMethodButtonProps {
  productId: string;
  productName: string;
  methodId: string;
  methodName: string;
  priceRowParams: PriceRowParams;
}

/** The + in a method cell the product does not offer (R18); no dialog. */
export function OfferMethodButton({
  productId,
  productName,
  methodId,
  methodName,
  priceRowParams,
}: OfferMethodButtonProps) {
  const offer = useOfferProductMethod(priceRowParams);

  const handleClick = async () => {
    if (offer.isPending) return;
    try {
      await offer.mutateAsync({ productId, methodId });
      toast.success(`${methodName} offered on "${productName}"`, TOAST_OPTS);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_OPTS);
    }
  };

  return (
    <button
      type="button"
      aria-label={`Offer ${methodName} on ${productName}`}
      aria-disabled={offer.isPending || undefined}
      aria-busy={offer.isPending || undefined}
      title="Offer on every active position"
      onClick={() => void handleClick()}
      className={ICON_BUTTON}
    >
      {offer.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <Plus className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}
